import { useCallback, useEffect, useState } from "react";
import { API } from 'aws-amplify';
import { Alert, Spinner, Button, Form, Col, Row } from "react-bootstrap";
import { getAuthToken } from '../../utils/AuthUtils.js';
import GenericGridComponent from "../../GenericGridComponent/GenericGridComponent";
import CompanyDetailsModal from "./CompanyDetailsModal/CompanyDetailsModal.js";
import './styles.scss';
import CompanyTickersModal from "./CompanyTickersModal/CompanyTickersModal.js";
import { FILTER_MNEMONICS } from "../../Constants.js";
import AddCompanyModal from "./AddCompanyModal/AddCompanyModal.js";

const CompanyDataTab = () => {
    const [companyData, setCompanyData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
    const [showCompanyTickersModal, setShowCompanyTickersModal] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);

    const actions = row => (
        <div className='edit-record'>
            <Button variant='primary view-details-btn' size='sm' className='mr-2' onClick={() => handleShowCompanyDetails(row)}>
                <i className="fa fa-eye"></i>
            </Button>
            <Button variant='primary edit-tickers-btn' size='sm' className='mr-2' onClick={() => handleShowTickerDetails(row)}>
                <i className="fa fa-edit" />
            </Button>
        </div>
    );

    const handleShowCompanyDetails = (row) => {
        setSelectedCompanyId(row.id);
        setShowCompanyDetailsModal(true);
    }

    const handleShowTickerDetails = (row) => {
        setSelectedCompanyId(row.id);
        setShowCompanyTickersModal(true);
    }

    const handleCloseCompanyDetailsModal = () => {
        setSelectedCompanyId("");
        setShowCompanyDetailsModal(false);
    }

    const handleCloseCompanyTickersModal = () => {
        setSelectedCompanyId("");
        setShowCompanyTickersModal(false);
    }

    const handleExport = async () => {
        const fileName = 'Company_Data';
        const XLSX = await import('xlsx');
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(companyData);
        XLSX.utils.book_append_sheet(wb, ws, 'Company Data');
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };

    const columns = [
        {
            key: 'id',
            name: 'Actions',
            sortable: false,
            filterable: false,
            frozen: true,
            minWidth: 60,
            formatter: ({ row }) => (
                actions(row)
            )
        },
        {
            key: 'companyName',
            name: 'Company Name',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 120,
        },
        {
            key: 'ticker',
            name: 'Ticker',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 80,
        },
        {
            key: 'exchange',
            name: 'Exchange',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 80,
        },
        {
            key: 'capIqCode',
            name: 'CapIQ code',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 120,
        },
        {
            key: 'companyId',
            name: 'Company ID',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 120,
        },
        {
            key: 'country',
            name: 'Country',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 120,
        },
        {
            key: 'industry',
            name: 'Industry',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 120,
        },
        {
            key: 'sector',
            name: 'Sector',
            sortable: true,
            filterable: true,
            frozen: true,
            minWidth: 120,
        },

    ];

    const getCompanyData = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);
            const authToken = await getAuthToken();
            const queryStringParameters = {
                index: selectedIndex
            }
            API.get('AnnualCompensations', '/company-data/companies', {
                headers: {
                    'Content-Type': 'application/json',
                    Token: authToken,
                },
                queryStringParameters
            }).then(response => {
                setCompanyData(response);
                setLoading(false);
            }).catch(error => {
                console.log(error);
                setError(true);
                setLoading(false);
                setErrorMessage("Error getting company data");

            })
        } catch (error) {
            console.log(error);
            setError(true);
            setLoading(false);
        }
    }, [selectedIndex])

    useEffect(() => {
        const mounted = true;
        if (mounted) {
            setCompanyData([]);
            setLoading(false);
            setError(false);
            setErrorMessage("");
            getCompanyData();
        }
    }, [getCompanyData])

    return (
        <div className="company-data-container">
            <AddCompanyModal
                show={showAddCompanyModal}
                onClose={() => setShowAddCompanyModal(false)}
            />
            <CompanyDetailsModal
                show={showCompanyDetailsModal}
                onClose={handleCloseCompanyDetailsModal}
                id={selectedCompanyId}
            />
            <CompanyTickersModal
                show={showCompanyTickersModal}
                onClose={handleCloseCompanyTickersModal}
                id={selectedCompanyId}
            />
            <Row>
                <Col md={2}>
                    <Form.Group controlId='index'>
                        <Form.Label>
                            Index
                        </Form.Label>
                        <Form.Select
                            value={selectedIndex}
                            onChange={(e) => setSelectedIndex(e.target.value)}
                        >
                            <option value={''}>No Index</option>
                            <option value={FILTER_MNEMONICS.SP500_INDEX}>S&P 500</option>
                            <option value={FILTER_MNEMONICS.SP1500_INDEX}>S&P 1500</option>
                            <option value={FILTER_MNEMONICS.SP400_INDEX}>S&P 400</option>
                            <option value={FILTER_MNEMONICS.RUSSELL3000_INDEX}>Russell 3000</option>
                            <option value={FILTER_MNEMONICS.NAREIT_INDEX}>NAREIT</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={2} style={{ "alignContent": "end" }}>
                    <Button
                        title="Add new Company"
                        varint="primary"
                        onClick={() => setShowAddCompanyModal(true)}>
                        Add Company
                    </Button>
                </Col>
            </Row>
            <div className="company-details-grid">
                {error && <Alert variant="danger" dismissible onClose={() => setError(false)}>{errorMessage}</Alert>}
                {loading && <Spinner animation="border" variant="primary" />}
                {companyData?.length > 0 && <GenericGridComponent
                    columns={columns}
                    data={companyData}
                    enableGridToCSV={true}
                    useCustomExportMethod={true}
                    customExportMethod={handleExport}
                    fileName={"Company_Data"}
                    itemsName={"Company Data"}
                    selectedCurrentPage={currentPage}
                    onCurrentPageSelect={page => setCurrentPage(page)}
                />}
            </div>
        </div>
    );
}

export default CompanyDataTab;